import { Badge } from '@cargoflow/ui/components/badge'
import { Button } from '@cargoflow/ui/components/button'
import { Calendar } from '@cargoflow/ui/components/calendar'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@cargoflow/ui/components/command'
import { Input } from '@cargoflow/ui/components/input'
import { Label } from '@cargoflow/ui/components/label'
import { Popover, PopoverContent, PopoverTrigger } from '@cargoflow/ui/components/popover'
import { Separator } from '@cargoflow/ui/components/separator'
import { Switch } from '@cargoflow/ui/components/switch'
import { LOAD_TYPES, type LoadType, VEHICLE_TYPES } from '@cargoflow/validators'
import { CheckIcon, PlusCircledIcon } from '@radix-ui/react-icons'
import { format } from 'date-fns'
import { Calendar as CalendarIcon, RotateCcw, X } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { match, P } from 'ts-pattern'
import { LocationSearch } from '@/components/location-search'
import type { NavigateFn } from '@/hooks/use-table-url-state'
import { isDateOnlyRangeInverted, toDateOnly } from '@/lib/form-utils'
import { getDateFormat } from '@/lib/format-date'
import { formatWeight } from '@/lib/format-weight'
import { cn } from '@/lib/utils'

export type MatchingFilters = {
  radius_km: number
  min_score: number | undefined
  origin_location_id: number | undefined
  origin_city: string | undefined
  origin_country_code: string | undefined
  destination_location_id: number | undefined
  destination_city: string | undefined
  destination_country_code: string | undefined
  truck_types: string[]
  load_type: LoadType | undefined
  loading_date_from: string | undefined
  loading_date_to: string | undefined
  max_weight: number | undefined
  adr_only: boolean
}

const RADIUS_OPTIONS = [50, 100, 150, 200, 300, 500] as const
const SCORE_OPTIONS = [40, 50, 60, 70, 80, 90] as const

/** Single source of truth for the radius used when none is selected. Radius is a
 * scoring input, so the list and the match detail must resolve it identically — a
 * mismatch makes the same pair score differently across the two views. */
export const DEFAULT_RADIUS_KM = 300

export function parseFiltersFromSearch(search: Record<string, unknown>): MatchingFilters {
  return {
    radius_km: (search.radius_km as number) ?? DEFAULT_RADIUS_KM,
    min_score: search.min_score as number | undefined,
    origin_location_id: search.origin_location_id as number | undefined,
    origin_city: search.origin_city as string | undefined,
    origin_country_code: search.origin_country_code as string | undefined,
    destination_location_id: search.destination_location_id as number | undefined,
    destination_city: search.destination_city as string | undefined,
    destination_country_code: search.destination_country_code as string | undefined,
    truck_types: (search.truck_types as string[]) ?? [],
    load_type: search.load_type as LoadType | undefined,
    loading_date_from: search.loading_date_from as string | undefined,
    loading_date_to: search.loading_date_to as string | undefined,
    max_weight: search.max_weight as number | undefined,
    adr_only: (search.adr_only as boolean) ?? false,
  }
}

type MatchingFiltersBarProps = {
  search: Record<string, unknown>
  navigate: NavigateFn
}

export function MatchingFiltersBar({ search, navigate }: MatchingFiltersBarProps) {
  const { t, i18n } = useTranslation('common')
  const filters = parseFiltersFromSearch(search)

  const updateFilter = (patch: Partial<MatchingFilters>) => {
    navigate({
      search: (prev) => ({ ...prev, ...patch, page: 1 }),
    })
  }

  const hasActiveFilters =
    filters.min_score != null ||
    filters.origin_location_id != null ||
    filters.destination_location_id != null ||
    filters.truck_types.length > 0 ||
    filters.load_type != null ||
    filters.loading_date_from != null ||
    filters.loading_date_to != null ||
    filters.max_weight != null ||
    filters.adr_only

  const resetFilters = () => {
    navigate({
      search: (prev) => ({
        ...prev,
        min_score: undefined,
        origin_location_id: undefined,
        origin_city: undefined,
        origin_country_code: undefined,
        destination_location_id: undefined,
        destination_city: undefined,
        destination_country_code: undefined,
        truck_types: undefined,
        load_type: undefined,
        loading_date_from: undefined,
        loading_date_to: undefined,
        max_weight: undefined,
        adr_only: undefined,
        page: 1,
      }),
    })
  }

  const dateRangeInvalid = isDateOnlyRangeInverted(
    filters.loading_date_from ? toDateOnly(filters.loading_date_from) : null,
    filters.loading_date_to ? toDateOnly(filters.loading_date_to) : null,
  )

  return (
    <div className="relative flex flex-wrap items-start gap-3 rounded-lg border p-3 pr-14 sm:pr-20">
      {/* Location group */}
      <div className="flex w-full flex-wrap items-center gap-2 sm:w-auto">
        <span className="text-xs font-medium text-muted-foreground">
          {t('matching.filters.group_location', { defaultValue: 'Location' })}
        </span>
        <FilterSelect
          title={t('matching.search_radius', { defaultValue: 'Radius' })}
          value={filters.radius_km != null ? `${filters.radius_km} km` : undefined}
          options={RADIUS_OPTIONS.map((v) => ({ label: `${v} km`, value: String(v) }))}
          selected={filters.radius_km != null ? [String(filters.radius_km)] : []}
          onSelect={(values) => updateFilter({ radius_km: Number(values[0]) })}
          multi={false}
        />
        <div className="w-full min-w-0 sm:w-[260px]">
          <LocationSearch
            placeholder={t('matching.filters.origin', { defaultValue: 'Origin city...' })}
            value={
              filters.origin_location_id != null && filters.origin_city
                ? {
                    city: filters.origin_city,
                    country_code: filters.origin_country_code ?? '',
                  }
                : null
            }
            onSelect={(location) =>
              updateFilter({
                origin_location_id: location.id,
                origin_city: location.city,
                origin_country_code: location.country_code,
              })
            }
            onClear={() =>
              updateFilter({
                origin_location_id: undefined,
                origin_city: undefined,
                origin_country_code: undefined,
              })
            }
          />
        </div>
        <div className="w-full min-w-0 sm:w-[260px]">
          <LocationSearch
            placeholder={t('matching.filters.destination', { defaultValue: 'Dest city...' })}
            value={
              filters.destination_location_id != null && filters.destination_city
                ? {
                    city: filters.destination_city,
                    country_code: filters.destination_country_code ?? '',
                  }
                : null
            }
            onSelect={(location) =>
              updateFilter({
                destination_location_id: location.id,
                destination_city: location.city,
                destination_country_code: location.country_code,
              })
            }
            onClear={() =>
              updateFilter({
                destination_location_id: undefined,
                destination_city: undefined,
                destination_country_code: undefined,
              })
            }
          />
        </div>
      </div>

      {/* Load details group */}
      <div className="flex w-full flex-wrap items-center gap-2 sm:w-auto">
        <span className="text-xs font-medium text-muted-foreground">
          {t('matching.filters.group_load', { defaultValue: 'Load' })}
        </span>
        <FilterSelect
          title={t('matching.filters.min_score', { defaultValue: 'Min Score' })}
          value={filters.min_score != null ? `≥ ${filters.min_score}%` : undefined}
          options={SCORE_OPTIONS.map((v) => ({ label: `≥ ${v}%`, value: String(v) }))}
          selected={filters.min_score != null ? [String(filters.min_score)] : []}
          onSelect={(values) =>
            updateFilter({ min_score: values.length > 0 ? Number(values[0]) : undefined })
          }
          multi={false}
        />
        <FilterSelect
          title={t('matching.filters.truck_type', { defaultValue: 'Truck Type' })}
          options={VEHICLE_TYPES.map((vt) => ({
            label: t(`fleet.types.${vt}`, { defaultValue: vt }),
            value: vt,
          }))}
          selected={filters.truck_types}
          onSelect={(values) => updateFilter({ truck_types: values.length > 0 ? values : [] })}
          multi
        />
        <FilterSelect
          title={t('matching.filters.load_type', { defaultValue: 'Load Type' })}
          options={LOAD_TYPES.map((lt) => ({
            label: t(`loads.fields.load_types.${lt}`, { defaultValue: lt }),
            value: lt,
          }))}
          selected={filters.load_type ? [filters.load_type] : []}
          onSelect={(values) =>
            updateFilter({ load_type: values.length > 0 ? (values[0] as LoadType) : undefined })
          }
          multi={false}
        />
        <div className="flex items-center">
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                className={cn(
                  'h-8 border-dashed',
                  filters.max_weight != null && 'rounded-r-none border-r-0',
                )}
              >
                <PlusCircledIcon className="size-4" />
                {t('matching.filters.max_weight', { defaultValue: 'Max Weight' })}
                {filters.max_weight != null && (
                  <>
                    <Separator orientation="vertical" className="mx-2 h-4" />
                    <Badge variant="secondary" className="rounded-sm px-1 font-normal">
                      ≤ {formatWeight(filters.max_weight)}
                    </Badge>
                  </>
                )}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[200px] p-3" align="start">
              <div className="flex flex-col gap-2">
                <Label className="text-xs">
                  {t('matching.filters.max_weight_label', { defaultValue: 'Max weight (kg)' })}
                </Label>
                <Input
                  type="number"
                  min={1}
                  step={1}
                  placeholder="e.g. 500"
                  defaultValue={filters.max_weight ?? ''}
                  onBlur={(e) => {
                    const val = e.target.value ? Number(e.target.value) : undefined
                    updateFilter({ max_weight: val })
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      const val = e.currentTarget.value ? Number(e.currentTarget.value) : undefined
                      updateFilter({ max_weight: val })
                    }
                  }}
                  className="h-8"
                />
              </div>
            </PopoverContent>
          </Popover>
          {filters.max_weight != null && (
            <Button
              variant="outline"
              size="sm"
              className="h-8 rounded-l-none border-dashed px-1.5"
              onClick={() => updateFilter({ max_weight: undefined })}
            >
              <X className="size-3.5 text-muted-foreground" />
            </Button>
          )}
        </div>
        <div className="flex items-center gap-2 rounded-md border border-dashed px-3 py-1">
          <Switch
            id="adr-only"
            checked={filters.adr_only}
            onCheckedChange={(checked) => updateFilter({ adr_only: checked === true })}
            className="scale-75"
          />
          <Label htmlFor="adr-only" className="cursor-pointer text-sm font-normal">
            ADR
          </Label>
        </div>
      </div>

      <Separator orientation="vertical" className="hidden h-8 self-center sm:block" />

      {/* Dates group */}
      <div className="flex w-full flex-col gap-1 sm:w-auto">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs font-medium text-muted-foreground">
            {t('matching.filters.group_dates', { defaultValue: 'Dates' })}
          </span>
          <FilterDatePicker
            placeholder={t('matching.filters.date_from', { defaultValue: 'From' })}
            value={filters.loading_date_from ? new Date(filters.loading_date_from) : undefined}
            onSelect={(date) =>
              updateFilter({ loading_date_from: date ? date.toISOString() : undefined })
            }
            locale={i18n.language}
          />
          <FilterDatePicker
            placeholder={t('matching.filters.date_to', { defaultValue: 'To' })}
            value={filters.loading_date_to ? new Date(filters.loading_date_to) : undefined}
            onSelect={(date) =>
              updateFilter({ loading_date_to: date ? date.toISOString() : undefined })
            }
            locale={i18n.language}
          />
        </div>
        {dateRangeInvalid && (
          <p className="text-xs font-medium text-destructive">
            {t('validation.date_range_end_before_start')}
          </p>
        )}
      </div>

      {/* Reset — pinned top-right */}
      {hasActiveFilters && (
        <Button
          variant="ghost"
          size="sm"
          className="absolute top-3 right-3 h-8 px-2"
          onClick={resetFilters}
        >
          <RotateCcw className="size-3.5" />
          {t('common.reset', { defaultValue: 'Reset' })}
        </Button>
      )}
    </div>
  )
}

// ─── Reusable filter primitives ───────────────────────────────────────────────

type FilterSelectProps = {
  title: string
  value?: string
  options: { label: string; value: string }[]
  selected: string[]
  onSelect: (values: string[]) => void
  multi: boolean
}

function FilterSelect({ title, value, options, selected, onSelect, multi }: FilterSelectProps) {
  const { t } = useTranslation('common')
  const selectedSet = new Set(selected)

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className="h-8 border-dashed">
          <PlusCircledIcon className="size-4" />
          {title}
          {selectedSet.size > 0 && (
            <>
              <Separator orientation="vertical" className="mx-2 h-4" />
              <div className="flex space-x-1">
                {match(selectedSet.size > 2)
                  .with(true, () => (
                    <Badge variant="secondary" className="rounded-sm px-1 font-normal">
                      {t('matching.filters.selected', { count: selectedSet.size })}
                    </Badge>
                  ))
                  .with(false, () =>
                    options
                      .filter((o) => selectedSet.has(o.value))
                      .map((o) => (
                        <Badge
                          variant="secondary"
                          key={o.value}
                          className="rounded-sm px-1 font-normal"
                        >
                          {value && !multi ? value : o.label}
                        </Badge>
                      )),
                  )
                  .exhaustive()}
              </div>
            </>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[200px] p-0" align="start">
        <Command>
          <CommandInput placeholder={title} />
          <CommandList>
            <CommandEmpty>{t('common.no_results')}</CommandEmpty>
            <CommandGroup>
              {options.map((option) => {
                const isSelected = selectedSet.has(option.value)
                return (
                  <CommandItem
                    key={option.value}
                    onSelect={() => {
                      if (multi) {
                        const next = new Set(selectedSet)
                        if (isSelected) next.delete(option.value)
                        else next.add(option.value)
                        onSelect(Array.from(next))
                      } else {
                        onSelect(isSelected ? [] : [option.value])
                      }
                    }}
                  >
                    <div
                      className={cn(
                        'flex size-4 items-center justify-center rounded-sm border border-primary',
                        isSelected
                          ? 'bg-primary text-primary-foreground'
                          : 'opacity-50 [&_svg]:invisible',
                      )}
                    >
                      <CheckIcon className="h-4 w-4 text-background" />
                    </div>
                    <span>{option.label}</span>
                  </CommandItem>
                )
              })}
            </CommandGroup>
            {selectedSet.size > 0 && (
              <>
                <CommandSeparator />
                <CommandGroup>
                  <CommandItem onSelect={() => onSelect([])} className="justify-center text-center">
                    {t('common.clear')}
                  </CommandItem>
                </CommandGroup>
              </>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}

type FilterDatePickerProps = {
  placeholder: string
  value: Date | undefined
  onSelect: (date: Date | undefined) => void
  locale: string
}

function FilterDatePicker({ placeholder, value, onSelect, locale }: FilterDatePickerProps) {
  return (
    <div className="flex items-center">
      <Popover>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            className={cn(
              'h-8 border-dashed',
              value && 'rounded-r-none border-r-0',
              !value && 'text-muted-foreground',
            )}
          >
            <CalendarIcon className="size-4" />
            {match(value)
              .with(P.nonNullable, (d) => format(d, getDateFormat(locale)))
              .otherwise(() => placeholder)}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <Calendar mode="single" captionLayout="dropdown" selected={value} onSelect={onSelect} />
        </PopoverContent>
      </Popover>
      {value && (
        <Button
          variant="outline"
          size="sm"
          className="h-8 rounded-l-none border-dashed px-1.5"
          onClick={() => onSelect(undefined)}
        >
          <X className="size-3.5 text-muted-foreground" />
        </Button>
      )}
    </div>
  )
}
